import * as yup from 'yup';
import type { FooterModel } from '@/redux/services/generatedApi/footer';
import {
  nonEmptyString,
  optionalString,
  linkItemSchema,
  languageItemSchema,
} from '@/util/yupSchemas';
import { SocialMedia } from '@/components/global/Footer';
import type { NullableOptional } from '@/types/util';

const logoModelSchema = yup
  .object({
    imageSrc: optionalString(),
    imageAlt: optionalString(),
    storeName: optionalString(),
    homepageTitle: optionalString(),
    clientCode: nonEmptyString().required(),
  })
  .concat(linkItemSchema);

const organizationInfoSchema = yup.object({
  logo: logoModelSchema.optional(),
});

const navigationLink = linkItemSchema.shape({
  url: nonEmptyString().required(),
  name: nonEmptyString().required(),
});

const socialMediaLinkSchema = linkItemSchema.shape({
  url: nonEmptyString().required(),
  name: yup
    .string()
    .test('is-valid-social-media', 'Invalid social media name', (value) => {
      if (!value) {
        return false;
      }
      return Object.values(SocialMedia).includes(value.toLowerCase() as SocialMedia);
    })
    .required(),
});

const footerModelSchema: yup.ObjectSchema<NullableOptional<FooterModel>> =
  yup.object({
    organizations: yup
      .array()
      .ensure()
      .compact((item) => !organizationInfoSchema.isValidSync(item))
      .of(organizationInfoSchema)
      .optional(),
    navigationLinks: yup
      .array()
      .ensure()
      .compact((item) => !navigationLink.isValidSync(item))
      .of(navigationLink)
      .optional(),
    languages: yup
      .array()
      .ensure()
      .compact((item) => !languageItemSchema.isValidSync(item))
      .of(languageItemSchema)
      .optional(),
    socialLinks: yup
      .array()
      .ensure()
      .compact((item) => !socialMediaLinkSchema.isValidSync(item))
      .of(socialMediaLinkSchema)
      .optional(),
  });

const parseFooterModel = (
  jsonString: string | undefined | null,
): NullableOptional<FooterModel> | null => {
  if (!jsonString || jsonString.trim() === '') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(jsonString);
    return footerModelSchema.validateSync(parsed);
  } catch (error) {
    console.warn('FooterModel validation failed:', error);
    return null;
  }
};

export default parseFooterModel;
